import React, { useEffect, useState } from "react";
import { message } from "antd";
import { Modal, ModalProps } from "react-bootstrap";
import { UserType } from "../../../store/types";
import "./ApiKeyModal.scss";

type APIKeyModalProps = ModalProps & {
  apiKey: string;
  user: UserType;
  onHide: () => void;
};

const API_KEY_VISIBLE_CHARS = 6;
const MASKED_API_KEY_FILLER = new Array(17).join("\u2022");

const getMaskedApiKey = (apiKey: string) => {
  if (apiKey.length <= API_KEY_VISIBLE_CHARS * 2) {
    return MASKED_API_KEY_FILLER;
  }

  const visibleStart = apiKey.slice(0, API_KEY_VISIBLE_CHARS);
  const visibleEnd = apiKey.slice(-API_KEY_VISIBLE_CHARS);

  return `${visibleStart}${MASKED_API_KEY_FILLER}${visibleEnd}`;
};

const ApiKeyModal: React.FC<APIKeyModalProps> = ({
  apiKey,
  user,
  onHide,
  ...props
}) => {
  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false);
  const teamLogo = `/assets/images/logos/${
    user.teamShort === "CC" ? "ZEBRA" : user.teamShort
  }.svg`;

  useEffect(() => {
    setIsApiKeyVisible(false);
  }, [apiKey]);

  const displayedApiKey = isApiKeyVisible ? apiKey : getMaskedApiKey(apiKey);
  const visibilityButtonLabel = isApiKeyVisible
    ? "Hide API key"
    : "Show API key";
  const visibilityIcon = isApiKeyVisible ? "mdi mdi-eye" : "mdi mdi-eye-off";

  const handleCopy = (token: string) => {
    navigator.clipboard.writeText(token);
    message.info(
      {
        content: "Token Copied to Clipboard",
        className: "antd-message-alert",
      },
      10
    );
  };

  return (
    <Modal
      {...props}
      animation={false}
      onHide={onHide}
      dialogClassName={"api-key-modal"}
      contentClassName={"api-key-modal-content"}
    >
      <Modal.Header closeButton className={"api-key-modal-header"}>
        <div className={"title"}>Refresh Token</div>
      </Modal.Header>
      <Modal.Body>
        <div className={"user-info"}>
          <img className={"team-logo"} src={teamLogo} alt="" />
          <div className={"user-detail"}>
            <div className={"label"}>Username</div>
            <div className={"value"}>{user.name}</div>
          </div>
          <div className={"user-detail email"}>
            <div className={"label"}>EMAIL USER</div>
            <div className={"value"}>{user.email}</div>
          </div>
        </div>
        <div className={"refresh-token"}>
          <div className={"label"}>
            <i className="mdi mdi-key-variant" />
            API Key
          </div>
          <div className={"token"}>
            <div className={"token-value"}>
              <span className={"token-text"}>{displayedApiKey}</span>
              <button
                type={"button"}
                className={"token-visibility"}
                onClick={() => setIsApiKeyVisible((isVisible) => !isVisible)}
                aria-label={visibilityButtonLabel}
                aria-pressed={isApiKeyVisible}
                title={visibilityButtonLabel}
              >
                <i className={visibilityIcon} />
              </button>
            </div>
            <div className={"token-action"}>
              <button
                type={"button"}
                className={"btn btn-blue"}
                onClick={() => handleCopy(apiKey)}
              >
                <i className="mdi mdi-content-copy" />
                Copy to clipboard
              </button>
            </div>
          </div>
        </div>
        <div className={"security-notice"}>
          <div className={"label"}>
            <i className="mdi mdi-alert" />
            Important:
          </div>
          <div className={"description"}>
            Store your new API key securely, as it grants access to your account
            resources.
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer className={"api-key-modal-footer"}>
        <button
          type={"button"}
          className="btn btn-outline-blue"
          onClick={onHide}
        >
          Close
        </button>
      </Modal.Footer>
    </Modal>
  );
};

export default ApiKeyModal;
